import {FC, useEffect, useState, useCallback, useMemo, useRef} from 'react'
import {getIssues, createIssue, updateIssue, deleteIssue} from './core/_requests'
import {AccountIssue, AdminOption, ISSUE_DEPARTMENTS} from './core/_models'
import {Avatar} from './core/ui'

// Calendar view of the Issues page: every issue with a `due_date` is a task on
// that day. Click a day to add a task, drag a task to another day (or back to
// the "unscheduled" tray) to move it, tick the circle to mark it done.

// Colour per department so a glance at the month shows who owns what.
export const DEPT_COLORS: Record<string, {color: string; bg: string}> = {
  customer_service: {color: '#2563eb', bg: '#eaf1ff'},
  development: {color: '#7c3aed', bg: '#f3edff'},
  accounting: {color: '#059669', bg: '#e6f8f1'},
  sales: {color: '#ea580c', bg: '#fff1e8'},
}
const NO_DEPT = {color: '#64748b', bg: '#f1f3f7'}
const deptColor = (d?: string | null) => (d && DEPT_COLORS[d]) || NO_DEPT
const deptLabel = (d?: string | null) => ISSUE_DEPARTMENTS.find((x) => x.value === d)?.label || 'ללא מחלקה'

const WEEKDAYS = ['א׳', 'ב׳', 'ג׳', 'ד׳', 'ה׳', 'ו׳', 'ש׳']
const MONTHS = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר']

// Local-time YYYY-MM-DD (never toISOString — that shifts the day in Israel).
const ymd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
const parseYmd = (s: string) => {
  const [y, m, d] = s.split('-').map(Number)
  return new Date(y, m - 1, d)
}
const addDays = (s: string, n: number) => {
  const d = parseYmd(s)
  d.setDate(d.getDate() + n)
  return ymd(d)
}
const prettyDay = (s: string) => {
  const d = parseYmd(s)
  return `יום ${WEEKDAYS[d.getDay()]} · ${d.getDate()} ב${MONTHS[d.getMonth()]}`
}

// Six Sunday-first weeks covering the month (Israeli week).
function monthGrid(year: number, month: number): string[] {
  const start = new Date(year, month, 1)
  start.setDate(start.getDate() - start.getDay())
  return Array.from({length: 42}, (_, i) => {
    const d = new Date(start)
    d.setDate(start.getDate() + i)
    return ymd(d)
  })
}

const DeptPicker: FC<{value: string; onChange: (v: string) => void}> = ({value, onChange}) => (
  <div className='d-flex flex-wrap gap-1'>
    {[{value: '', label: 'ללא'}, ...ISSUE_DEPARTMENTS].map((d) => {
      const c = deptColor(d.value)
      const on = value === d.value
      return (
        <button
          key={d.value || 'none'}
          type='button'
          className='cal-dept-btn'
          style={{
            background: on ? c.color : c.bg,
            color: on ? '#fff' : c.color,
          }}
          onClick={() => onChange(d.value)}
        >
          {d.label}
        </button>
      )
    })}
  </div>
)

// ---- a single task chip (draggable) ----
const TaskChip: FC<{
  task: AccountIssue
  overdue: boolean
  onOpen: () => void
  onToggleDone: () => void
  onDragStart: (id: string) => void
  onDragEnd: () => void
}> = ({task, overdue, onOpen, onToggleDone, onDragStart, onDragEnd}) => {
  const c = deptColor(task.department)
  const done = task.status === 'done'
  return (
    <div
      className={`cal-task${done ? ' done' : ''}${overdue ? ' overdue' : ''}`}
      style={{background: c.bg, borderInlineStartColor: c.color}}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', task._id)
        e.dataTransfer.effectAllowed = 'move'
        onDragStart(task._id)
      }}
      onDragEnd={onDragEnd}
      onClick={(e) => {
        e.stopPropagation()
        onOpen()
      }}
      title={`${task.title}\n${deptLabel(task.department)}${task.assigned_to ? ' · ' + task.assigned_to.username : ''}`}
    >
      <span
        className='cal-check'
        style={{borderColor: c.color, background: done ? c.color : 'transparent'}}
        onClick={(e) => {
          e.stopPropagation()
          onToggleDone()
        }}
      >
        {done && <i className='bi bi-check' />}
      </span>
      <span className='cal-task-title'>{task.title || '(ללא כותרת)'}</span>
      {task.urgency === 'urgent' && <i className='bi bi-lightning-fill' style={{color: '#f1416c', fontSize: 11}} />}
      {task.assigned_to && <Avatar name={task.assigned_to.username} size={18} />}
    </div>
  )
}

// ---- quick-add box that opens inside a day cell ----
const QuickAdd: FC<{
  day: string
  defaultDept: string
  admins: AdminOption[]
  onSave: (title: string, dept: string, assignee: string) => Promise<void>
  onClose: () => void
}> = ({day, defaultDept, admins, onSave, onClose}) => {
  const [title, setTitle] = useState('')
  const [dept, setDept] = useState(defaultDept)
  const [assignee, setAssignee] = useState('')
  const [saving, setSaving] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node) && !title.trim()) onClose()
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [title, onClose])

  const save = async () => {
    if (!title.trim() || saving) return
    setSaving(true)
    try {
      await onSave(title.trim(), dept, assignee)
      setTitle('') // stay open so several tasks can be typed in a row
    } finally {
      setSaving(false)
    }
  }

  const staff = dept ? admins.filter((a) => a.departments?.includes(dept)) : admins
  return (
    <div className='cal-quickadd' ref={ref} onClick={(e) => e.stopPropagation()}>
      <div style={{fontSize: 11, color: '#8b93a7', marginBottom: 6}}>{prettyDay(day)}</div>
      <input
        autoFocus
        className='iss-input'
        placeholder='מה צריך לעשות? (Enter לשמירה)'
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') save()
          if (e.key === 'Escape') onClose()
        }}
      />
      <div style={{fontSize: 11, color: '#8b93a7', margin: '8px 0 4px'}}>מחלקה</div>
      <DeptPicker
        value={dept}
        onChange={(v) => {
          setDept(v)
          setAssignee('')
        }}
      />
      <div className='d-flex gap-2 align-items-center mt-2'>
        <select
          className='iss-input'
          style={{padding: '7px 10px'}}
          value={assignee}
          onChange={(e) => setAssignee(e.target.value)}
        >
          <option value=''>ללא נציג</option>
          {staff.map((a) => (
            <option key={a._id} value={a._id}>
              {a.username}
            </option>
          ))}
        </select>
        <button className='iss-btn iss-btn-primary iss-btn-sm' onClick={save} disabled={!title.trim() || saving}>
          {saving ? <span className='spinner-border spinner-border-sm' /> : 'הוסף'}
        </button>
      </div>
    </div>
  )
}

// ---- task detail / edit modal ----
const TaskModal: FC<{
  task: AccountIssue
  admins: AdminOption[]
  onPatch: (patch: Parameters<typeof updateIssue>[1]) => Promise<void>
  onDelete: () => Promise<void>
  onClose: () => void
}> = ({task, admins, onPatch, onDelete, onClose}) => {
  const [title, setTitle] = useState(task.title)
  const [desc, setDesc] = useState(task.description)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const today = ymd(new Date())
  const staff = task.department ? admins.filter((a) => a.departments?.includes(task.department!)) : admins
  const staffWithCur =
    task.assigned_to && !staff.find((a) => a._id === task.assigned_to!._id) ? [task.assigned_to, ...staff] : staff

  const commitText = () => {
    const patch: {title?: string; description?: string} = {}
    if (title.trim() && title.trim() !== task.title) patch.title = title.trim()
    if (desc !== task.description) patch.description = desc
    if (Object.keys(patch).length) onPatch(patch)
  }

  return (
    <div className='cal-modal-bg' onClick={() => (commitText(), onClose())}>
      <div className='cal-modal' dir='rtl' onClick={(e) => e.stopPropagation()}>
        <div className='d-flex align-items-center gap-2 mb-3'>
          <span
            className='cal-check lg'
            style={{
              borderColor: deptColor(task.department).color,
              background: task.status === 'done' ? deptColor(task.department).color : 'transparent',
            }}
            onClick={() => onPatch({status: task.status === 'done' ? 'open' : 'done'})}
            title={task.status === 'done' ? 'סמן כלא בוצע' : 'סמן כבוצע'}
          >
            {task.status === 'done' && <i className='bi bi-check' />}
          </span>
          <input
            className='cal-title-input'
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={commitText}
            onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
          />
          <i className='bi bi-x-lg' style={{cursor: 'pointer', color: '#b3b9c7'}} onClick={() => (commitText(), onClose())} />
        </div>

        <div className='cal-field'>
          <label>תאריך</label>
          <div className='d-flex flex-wrap gap-1 align-items-center'>
            <input
              type='date'
              className='iss-input'
              style={{width: 'auto', padding: '6px 10px'}}
              value={task.due_date || ''}
              onChange={(e) => onPatch({due_date: e.target.value || null})}
            />
            <button className='iss-btn iss-btn-ghost iss-btn-sm' onClick={() => onPatch({due_date: today})}>
              היום
            </button>
            <button className='iss-btn iss-btn-ghost iss-btn-sm' onClick={() => onPatch({due_date: addDays(today, 1)})}>
              מחר
            </button>
            <button
              className='iss-btn iss-btn-ghost iss-btn-sm'
              onClick={() => onPatch({due_date: addDays(task.due_date || today, 7)})}
            >
              +שבוע
            </button>
            {task.due_date && (
              <button className='iss-btn iss-btn-ghost iss-btn-sm' onClick={() => onPatch({due_date: null})}>
                הסר מהיומן
              </button>
            )}
          </div>
        </div>

        <div className='cal-field'>
          <label>מחלקה</label>
          <DeptPicker
            value={task.department || ''}
            onChange={(v) =>
              onPatch({
                department: v || null,
                // clear an assignee who isn't in the new department
                ...(v && task.assigned_to && !admins.find((a) => a._id === task.assigned_to!._id)?.departments?.includes(v)
                  ? {assigned_to: null}
                  : {}),
              })
            }
          />
        </div>

        <div className='cal-field'>
          <label>נציג</label>
          <select
            className='iss-input'
            style={{padding: '7px 10px'}}
            value={task.assigned_to?._id || ''}
            onChange={(e) => onPatch({assigned_to: e.target.value || null})}
          >
            <option value=''>ללא נציג</option>
            {staffWithCur.map((a) => (
              <option key={a._id} value={a._id}>
                {a.username}
              </option>
            ))}
          </select>
        </div>

        <div className='cal-field'>
          <label>הערות</label>
          <textarea
            className='iss-textarea'
            placeholder='פרטים נוספים (אופציונלי)'
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            onBlur={commitText}
          />
        </div>

        <div className='d-flex align-items-center gap-2 mt-3'>
          {confirmDelete ? (
            <>
              <span style={{fontSize: 13, color: '#b91c1c'}}>למחוק את המשימה?</span>
              <button className='iss-btn iss-btn-sm' style={{background: '#f1416c', color: '#fff'}} onClick={onDelete}>
                מחק
              </button>
              <button className='iss-btn iss-btn-ghost iss-btn-sm' onClick={() => setConfirmDelete(false)}>
                ביטול
              </button>
            </>
          ) : (
            <button className='iss-iconbtn danger' onClick={() => setConfirmDelete(true)} title='מחק משימה'>
              <i className='bi bi-trash' />
            </button>
          )}
          <span className='ms-auto' style={{fontSize: 11, color: '#b3b9c7'}}>
            {task.opened_by && <>נוצר ע״י {task.opened_by}</>}
          </span>
        </div>
      </div>
    </div>
  )
}

const IssuesCalendar: FC<{admins: AdminOption[]}> = ({admins}) => {
  const today = ymd(new Date())
  const [cursor, setCursor] = useState(() => {
    const d = new Date()
    return {y: d.getFullYear(), m: d.getMonth()}
  })
  const [dept, setDept] = useState('') // '' = all departments
  const [tasks, setTasks] = useState<AccountIssue[]>([])
  const [backlog, setBacklog] = useState<AccountIssue[]>([])
  const [loading, setLoading] = useState(true)
  const [adding, setAdding] = useState<string | null>(null) // day with open quick-add
  const [openId, setOpenId] = useState<string | null>(null)
  const [dragId, setDragId] = useState<string | null>(null)
  const [overDay, setOverDay] = useState<string | null>(null)
  const [showBacklog, setShowBacklog] = useState(true)
  const [error, setError] = useState('')

  const days = useMemo(() => monthGrid(cursor.y, cursor.m), [cursor])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [cal, bl] = await Promise.all([
        getIssues({from: days[0], to: days[41], department: dept || undefined}),
        getIssues({unscheduled: 1, status: 'open', department: dept || undefined}),
      ])
      setTasks(cal.data.issues || [])
      setBacklog(bl.data.issues || [])
    } finally {
      setLoading(false)
    }
  }, [days, dept])

  useEffect(() => {
    load()
  }, [load])

  const byDay = useMemo(() => {
    const map: Record<string, AccountIssue[]> = {}
    tasks.forEach((t) => {
      if (!t.due_date) return
      ;(map[t.due_date] = map[t.due_date] || []).push(t)
    })
    // open first, then done
    Object.values(map).forEach((list) => list.sort((a, b) => Number(a.status === 'done') - Number(b.status === 'done')))
    return map
  }, [tasks])

  const all = useMemo(() => [...tasks, ...backlog], [tasks, backlog])
  const openTask = all.find((t) => t._id === openId) || null
  const overdue = tasks.filter((t) => t.due_date && t.due_date < today && t.status !== 'done')

  const flash = (msg: string) => {
    setError(msg)
    setTimeout(() => setError(''), 3000)
  }

  // Put an updated task into the right list (calendar vs unscheduled tray).
  const place = (t: AccountIssue) => {
    setTasks((p) => {
      const rest = p.filter((x) => x._id !== t._id)
      return t.due_date ? [...rest, t] : rest
    })
    setBacklog((p) => {
      const rest = p.filter((x) => x._id !== t._id)
      return !t.due_date && t.status !== 'done' ? [t, ...rest] : rest
    })
  }

  // Optimistic patch: show the change at once, roll back if the server refuses.
  const patch = async (id: string, data: Parameters<typeof updateIssue>[1]) => {
    const before = all.find((t) => t._id === id)
    if (!before) return
    place({...before, ...(data as any)})
    try {
      place((await updateIssue(id, data)).data.issue)
    } catch {
      place(before)
      flash('השמירה נכשלה — נסה שוב')
    }
  }

  const moveTo = (id: string, day: string | null) => {
    const t = all.find((x) => x._id === id)
    if (!t || (t.due_date || null) === day) return
    patch(id, {due_date: day})
  }

  const addTask = async (day: string, title: string, department: string, assignee: string) => {
    try {
      const res = await createIssue({
        title,
        due_date: day,
        department: department || undefined,
        assigned_to: assignee || undefined,
      })
      place(res.data.issue)
    } catch {
      flash('יצירת המשימה נכשלה')
    }
  }

  const rollOverdueToToday = async () => {
    await Promise.all(overdue.map((t) => patch(t._id, {due_date: today})))
  }

  const goMonth = (delta: number) =>
    setCursor((c) => {
      const d = new Date(c.y, c.m + delta, 1)
      return {y: d.getFullYear(), m: d.getMonth()}
    })

  const dropProps = (day: string | null) => ({
    onDragOver: (e: React.DragEvent) => {
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'
      if (overDay !== (day || 'backlog')) setOverDay(day || 'backlog')
    },
    onDragLeave: (e: React.DragEvent) => {
      if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) setOverDay(null)
    },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault()
      const id = e.dataTransfer.getData('text/plain') || dragId
      setOverDay(null)
      setDragId(null)
      if (id) moveTo(id, day)
    },
  })

  const chip = (t: AccountIssue) => (
    <TaskChip
      key={t._id}
      task={t}
      overdue={!!t.due_date && t.due_date < today && t.status !== 'done'}
      onOpen={() => setOpenId(t._id)}
      onToggleDone={() => patch(t._id, {status: t.status === 'done' ? 'open' : 'done'})}
      onDragStart={setDragId}
      onDragEnd={() => {
        setDragId(null)
        setOverDay(null)
      }}
    />
  )

  return (
    <div className='cal-wrap'>
      {/* Toolbar: month nav + department filter */}
      <div className='d-flex flex-wrap align-items-center gap-2 mb-3'>
        <div className='d-flex align-items-center gap-1'>
          <button className='iss-iconbtn' onClick={() => goMonth(-1)} title='חודש קודם'>
            <i className='bi bi-chevron-right' />
          </button>
          <button className='iss-iconbtn' onClick={() => goMonth(1)} title='חודש הבא'>
            <i className='bi bi-chevron-left' />
          </button>
          <h2 style={{fontSize: 20, fontWeight: 800, margin: '0 8px', minWidth: 140}}>
            {MONTHS[cursor.m]} {cursor.y}
          </h2>
          <button
            className='iss-btn iss-btn-ghost iss-btn-sm'
            onClick={() => {
              const d = new Date()
              setCursor({y: d.getFullYear(), m: d.getMonth()})
            }}
          >
            היום
          </button>
          {loading && <span className='spinner-border spinner-border-sm text-primary ms-2' />}
        </div>

        <div className='d-flex flex-wrap gap-1 ms-auto'>
          <button className={`cal-filter ${dept === '' ? 'on' : ''}`} onClick={() => setDept('')}>
            כל המחלקות
          </button>
          {ISSUE_DEPARTMENTS.map((d) => {
            const c = deptColor(d.value)
            const on = dept === d.value
            return (
              <button
                key={d.value}
                className={`cal-filter ${on ? 'on' : ''}`}
                style={on ? {background: c.color, color: '#fff', borderColor: c.color} : {color: c.color}}
                onClick={() => setDept(on ? '' : d.value)}
              >
                <span className='iss-dot' style={{background: on ? '#fff' : c.color, width: 7, height: 7}} />
                {d.label}
              </button>
            )
          })}
          <button
            className={`iss-btn iss-btn-ghost iss-btn-sm ${showBacklog ? 'active' : ''}`}
            onClick={() => setShowBacklog(!showBacklog)}
          >
            <i className='bi bi-inbox' /> לא מתוזמנות ({backlog.length})
          </button>
        </div>
      </div>

      {overdue.length > 0 && (
        <div className='cal-overdue-bar'>
          <i className='bi bi-exclamation-circle' />
          {overdue.length} משימות שעבר זמנן ולא בוצעו
          <button className='iss-btn iss-btn-sm' onClick={rollOverdueToToday}>
            העבר הכל להיום
          </button>
        </div>
      )}
      {error && <div className='cal-error'>{error}</div>}

      <div className='cal-layout'>
        {/* Month grid */}
        <div className='cal-grid-scroll'>
          <div className='cal-grid'>
            {WEEKDAYS.map((w) => (
              <div key={w} className='cal-weekday'>
                {w}
              </div>
            ))}
            {days.map((day) => {
              const d = parseYmd(day)
              const inMonth = d.getMonth() === cursor.m
              const list = byDay[day] || []
              const isShabbat = d.getDay() === 6
              return (
                <div
                  key={day}
                  className={[
                    'cal-day',
                    inMonth ? '' : 'out',
                    day === today ? 'today' : '',
                    day < today ? 'past' : '',
                    isShabbat ? 'shabbat' : '',
                    overDay === day ? 'drop' : '',
                  ].join(' ')}
                  onClick={() => setAdding(day)}
                  {...dropProps(day)}
                >
                  <div className='cal-day-head'>
                    <span className='cal-day-num'>{d.getDate()}</span>
                    <i className='bi bi-plus cal-day-plus' />
                  </div>
                  <div className='cal-day-tasks'>{list.map(chip)}</div>
                  {adding === day && (
                    <QuickAdd
                      day={day}
                      defaultDept={dept}
                      admins={admins}
                      onSave={(title, dp, as) => addTask(day, title, dp, as)}
                      onClose={() => setAdding(null)}
                    />
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* Unscheduled tray: drag from here onto a day, or drop here to unschedule */}
        {showBacklog && (
          <div className={`cal-backlog ${overDay === 'backlog' ? 'drop' : ''}`} {...dropProps(null)}>
            <div className='cal-backlog-head'>
              <i className='bi bi-inbox me-1' />
              לא מתוזמנות
              <div style={{fontSize: 11, color: '#8b93a7', fontWeight: 500}}>גרור משימה ליום ביומן</div>
            </div>
            <div className='cal-backlog-list'>
              {backlog.map(chip)}
              {backlog.length === 0 && (
                <div style={{fontSize: 12, color: '#b3b9c7', textAlign: 'center', padding: '16px 0'}}>
                  הכל מתוזמן 🎉
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div style={{fontSize: 12, color: '#8b93a7', marginTop: 10}}>
        <i className='bi bi-lightbulb me-1' />
        לחץ על יום כדי להוסיף משימה · גרור משימה כדי להעביר אותה ליום אחר · לחץ על העיגול כדי לסמן כבוצע
      </div>

      {openTask && (
        <TaskModal
          key={openTask._id}
          task={openTask}
          admins={admins}
          onPatch={(data) => patch(openTask._id, data)}
          onDelete={async () => {
            try {
              await deleteIssue(openTask._id)
              setTasks((p) => p.filter((x) => x._id !== openTask._id))
              setBacklog((p) => p.filter((x) => x._id !== openTask._id))
              setOpenId(null)
            } catch {
              flash('המחיקה נכשלה')
            }
          }}
          onClose={() => setOpenId(null)}
        />
      )}
    </div>
  )
}

export default IssuesCalendar
